/**
 * Stateful demo wrappers for the `/components` gallery.
 *
 * The showcase manifest used to pass static mocks (fixed `value` +
 * no-op `onChange`) to the controlled filter / date-picker widgets,
 * which rendered them inert in the gallery: selects snapped back,
 * chips didn't toggle, sliders didn't move, search didn't type.
 *
 * Each wrapper here holds local `useState` so every widget is fully
 * interactive in the gallery while the underlying components stay
 * 100% controlled (no API changes). Internal helper module — NOT
 * exported from the lib barrel, so it is invisible to the
 * manifest-parity check.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  EmbeddedFilterBar,
  ActiveFiltersBar,
  ChipFilter,
  RangeSlider,
  SearchFilter,
  DatePresetFilter,
  AutocompleteFilter,
  DateRangePicker,
  CalendarPicker,
  RelativeDatePicker,
  DateGroupToggle,
  MultiSelectDropdown,
  SegmentedTabs,
  SavedFilters,
  MeterBar,
  StackedShareBar,
  VerticalBar,
  LineChart,
  LineChartT,
  MultiLineT,
  PieChart,
  ComparisonTable,
} from '../lib/index.js';

import {
  embeddedFilters,
  barData,
  lineData,
  multiLineSeries,
  multiLineLabels,
  pieData,
  chipOptions,
  datePresetOptions,
  regionOptions,
  multiSelectOptions,
  savedFilterPresets,
  categoryTabs,
  countryTabs,
} from './mock-data.js';

// ── Filters ─────────────────────────────────────────────────

export function DemoEmbeddedFilterBar() {
  const [values, setValues] = useState({ period: '2025 Q4' });
  return (
    <EmbeddedFilterBar
      filters={embeddedFilters}
      values={values}
      onChange={(key, val) => setValues(v => ({ ...v, [key]: val }))}
      onClear={() => setValues({})}
    />
  );
}

const ACTIVE_FILTER_ITEMS = [
  { key: 'period', label: 'Period', value: '2025 Q4' },
  { key: 'region', label: 'Region', value: 'LATAM' },
];

export function DemoActiveFiltersBar() {
  const [items, setItems] = useState(ACTIVE_FILTER_ITEMS);
  if (items.length === 0) {
    return (
      <button
        onClick={() => setItems(ACTIVE_FILTER_ITEMS)}
        className="h-6 rounded-md border border-neutral-200 px-2 text-[11px] font-semibold text-neutral-500 hover:border-accent/50 hover:text-accent transition-colors">
        Restaurar demo
      </button>
    );
  }
  return (
    <ActiveFiltersBar
      items={items.map(it => ({
        ...it,
        onRemove: () => setItems(prev => prev.filter(p => p.key !== it.key)),
      }))}
      onClearAll={() => setItems([])}
    />
  );
}

export function DemoChipFilter() {
  const [selected, setSelected] = useState(['Laptops']);
  return (
    <ChipFilter
      label="Categories"
      options={chipOptions}
      selected={selected}
      onToggle={(opt) =>
        setSelected(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt])
      }
      multi
    />
  );
}

export function DemoRangeSlider() {
  const [value, setValue] = useState([20, 75]);
  return <RangeSlider label="Price" min={0} max={100} value={value} onChange={setValue} />;
}

export function DemoSearchFilter() {
  const [value, setValue] = useState('');
  return <SearchFilter label="SKU search" value={value} placeholder="Search SKU…" onChange={setValue} />;
}

export function DemoDatePresetFilter() {
  const [selected, setSelected] = useState('30d');
  return (
    <DatePresetFilter label="Period" options={datePresetOptions} selected={selected} onSelect={setSelected} />
  );
}

export function DemoAutocompleteFilter() {
  const [value, setValue] = useState(null);
  return <AutocompleteFilter label="Region" options={regionOptions} value={value} onChange={setValue} />;
}

// ── Dates ───────────────────────────────────────────────────

// Full-feature DateRangePicker demo — mirrors what the PRODUCT popover
// ships (dashboard-render's DateRangePicker wrapper on the gating
// reports): gating-style preset chips, and the in-popover dual-handle
// date slider with zoom (bounds = the real gating data domain,
// 2021-01-04 → 2026-06-08; the track re-anchors to the committed
// selection doubled, clamped to the bounds — same model as
// ``expandBoundsForSelection`` in dashboard-render/date-slider-core).
// The slider drives a PENDING window (pills + calendars follow live);
// "Aplicar" commits it, dismissing discards it.
//
// The real slider lives in ``@pervasivemind/dashboard-render`` which
// depends on THIS package — the gallery demo can't import it without a
// cycle, so a compact stand-in with the same mechanics (uncontrolled-
// while-dragging + nearest-handle routing, primary/purple styling per
// the date-picker color exception) lives below.

const DEMO_DAY_MS = 86400000;
const DEMO_BOUNDS = ['2021-01-04', '2026-06-08'];

function demoIsoToDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function demoDateToIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// HP fiscal quarters start Nov / Feb / May / Aug (mirror of the
// engine's ``hp_fiscal_q`` presets used by the gating reports).
function demoFiscalQStart(offsetBack) {
  const today = new Date();
  const fqIndex = Math.floor(((today.getMonth() - 10 + 12) % 12) / 3);
  const fy = today.getFullYear() + (today.getMonth() >= 10 ? 1 : 0);
  let idx = fy * 4 + fqIndex - offsetBack;
  const qy = Math.floor(idx / 4);
  const q = idx % 4;
  const month = [10, 1, 4, 7][q];
  return new Date(qy + (q === 0 ? -1 : 0), month, 1);
}

const DEMO_PRESETS = [
  { id: 'all', label: 'All' },
  { id: 'hp_q_actual', label: 'Current Q' },
  { id: 'hp_last_q', label: 'Last Q' },
  { id: 'hp_last_4_q', label: 'Last 4 Q' },
  { id: 'hp_last_8_q', label: 'Last 8 Q' },
];

function demoResolvePreset(id) {
  const today = new Date();
  switch (id) {
    case 'all':
      return [demoIsoToDate(DEMO_BOUNDS[0]), demoIsoToDate(DEMO_BOUNDS[1])];
    case 'hp_q_actual':
      return [demoFiscalQStart(0), today];
    case 'hp_last_q':
      return [demoFiscalQStart(1), demoFiscalQStart(0)];
    case 'hp_last_4_q':
      return [demoFiscalQStart(3), today];
    case 'hp_last_8_q':
      return [demoFiscalQStart(7), today];
    default:
      return null;
  }
}

/** Zoomed track window: committed selection widened to double its
 *  length, clamped to DEMO_BOUNDS (no selection → full bounds). */
function demoZoomWindow(selStart, selEnd) {
  const bLo = demoIsoToDate(DEMO_BOUNDS[0]).getTime();
  const bHi = demoIsoToDate(DEMO_BOUNDS[1]).getTime();
  if (!selStart || !selEnd) return [bLo, bHi];
  const sLo = Math.max(Math.min(selStart.getTime(), selEnd.getTime()), bLo);
  const sHi = Math.min(Math.max(selStart.getTime(), selEnd.getTime()), bHi);
  if (sLo > sHi) return [bLo, bHi];
  const len = Math.max(sHi - sLo, 7 * DEMO_DAY_MS);
  let lo = sLo - len / 2;
  let hi = sHi + len / 2;
  if (lo < bLo) { hi += bLo - lo; lo = bLo; }
  if (hi > bHi) { lo -= hi - bHi; hi = bHi; }
  if (lo < bLo) lo = bLo;
  return [lo, hi];
}

/** ISO-week Mondays covering [loMs, hiMs] — the slider's snap points.
 *  Arithmetic runs on UTC epoch-days (mirror of date-slider-core):
 *  local-time ms strides drift across DST transitions, shifting the
 *  civil date by one. */
function demoWeekDomain(loMs, hiMs) {
  const toUtc = (ms) => {
    const d = new Date(ms);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  };
  const utcLo = toUtc(loMs);
  const utcHi = toUtc(hiMs);
  const dow = new Date(utcLo).getUTCDay(); // 0 = Sunday
  let t = utcLo - ((dow + 6) % 7) * DEMO_DAY_MS;
  const out = [];
  while (t <= utcHi) {
    const d = new Date(t);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    out.push(`${y}-${m}-${dd}`);
    t += 7 * DEMO_DAY_MS;
  }
  return out;
}

/** Nearest domain index for a date (clamped). */
function demoNearestIndex(domain, d) {
  const target = d.getTime();
  let best = 0;
  let bestDelta = Infinity;
  for (let i = 0; i < domain.length; i++) {
    const delta = Math.abs(demoIsoToDate(domain[i]).getTime() - target);
    if (delta < bestDelta) { bestDelta = delta; best = i; }
  }
  return best;
}

/** Compact purple stand-in for the product's in-popover date slider:
 *  same mechanics (uncontrolled-while-dragging, sorted-pair emission,
 *  nearest-handle routing, no renders per pixel), primary styling per
 *  the date-picker color exception. Extremes of the zoomed track as
 *  labels — the selection readout lives in the Inicio/Fin pills. */
function DemoPopoverDateSlider({ domain, lo, hi, onChange }) {
  const maxIdx = domain.length - 1;
  const [local, setLocal] = useState([lo, hi]);
  const dragging = useRef(false);
  const synced = useRef([lo, hi]);
  if (!dragging.current && (synced.current[0] !== lo || synced.current[1] !== hi)) {
    synced.current = [lo, hi];
    setLocal([lo, hi]);
  }
  const [rawLo, rawHi] = local;
  const dispLo = Math.min(rawLo, rawHi);
  const dispHi = Math.max(rawLo, rawHi);
  const pctLo = maxIdx === 0 ? 0 : (dispLo / maxIdx) * 100;
  const pctHi = maxIdx === 0 ? 100 : (dispHi / maxIdx) * 100;

  const move = (which, next) => {
    dragging.current = true;
    setLocal(which === 0 ? [next, rawHi] : [rawLo, next]);
    const a = which === 0 ? next : rawLo;
    const b = which === 1 ? next : rawHi;
    onChange(Math.min(a, b), Math.max(a, b));
  };
  const commit = () => { dragging.current = false; setLocal([dispLo, dispHi]); };

  const [nearest, setNearest] = useState(null);
  const nearestRef = useRef(null);
  const updateNearest = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    const dLo = Math.abs(pct - pctLo);
    const dHi = Math.abs(pct - pctHi);
    let next;
    if (dLo < dHi) next = 0;
    else if (dHi < dLo) next = 1;
    else if (pct < pctLo) next = 0;
    else if (pct > pctHi) next = 1;
    else next = (pctLo + pctHi) / 2 > 50 ? 0 : 1;
    if (nearestRef.current !== next) { nearestRef.current = next; setNearest(next); }
  };
  const clearNearest = () => {
    if (nearestRef.current !== null) { nearestRef.current = null; setNearest(null); }
  };

  // Adjacent (never overlapping) thumbs — same visual-offset pattern as
  // the product slider (dashboard-render/date-slider-core.tsx): when the
  // 16px balls collide, LO nudges left and HI right, edges touching.
  // Visual only; routing math keeps the un-shifted percents. Track width
  // via ResizeObserver (layout changes only — never per drag frame).
  const trackRef = useRef(null);
  const [trackW, setTrackW] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setTrackW((prev) => (prev === w ? prev : w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const THUMB_PX = 16;
  const gapPx = trackW > 0 ? ((pctHi - pctLo) / 100) * trackW : Number.POSITIVE_INFINITY;
  const sepPx = gapPx < THUMB_PX ? Math.min((THUMB_PX - gapPx) / 2, THUMB_PX / 2) : 0;
  const loOnTop = rawLo === rawHi && rawLo > maxIdx / 2;
  const topInput = nearest !== null ? nearest : (loOnTop ? 0 : 1);

  const inputClass = 'absolute top-0 h-6 w-full cursor-pointer opacity-0';
  const thumbClass = 'absolute top-0.5 h-4 w-4 rounded-full border-2 border-primary bg-white shadow-sm pointer-events-none';

  return (
    <div className="w-full">
      <div ref={trackRef} className="relative h-6" onPointerMove={updateNearest} onPointerDownCapture={updateNearest} onPointerLeave={clearNearest}>
        <div className="absolute left-0 right-0 top-2 h-1.5 rounded-full bg-neutral-100" />
        <div className="absolute top-2 h-1.5 rounded-full bg-primary" style={{ left: `${pctLo}%`, width: `${pctHi - pctLo}%` }} />
        <input type="range" min={0} max={maxIdx} step={1} value={rawLo}
          aria-label="Desde" aria-valuetext={domain[dispLo]}
          onChange={e => move(0, Number(e.target.value))}
          onPointerUp={commit} onKeyUp={commit} onBlur={commit}
          className={inputClass} style={{ zIndex: topInput === 0 ? 4 : 3 }} />
        <input type="range" min={0} max={maxIdx} step={1} value={rawHi}
          aria-label="Hasta" aria-valuetext={domain[dispHi]}
          onChange={e => move(1, Number(e.target.value))}
          onPointerUp={commit} onKeyUp={commit} onBlur={commit}
          className={inputClass} style={{ zIndex: topInput === 1 ? 4 : 3 }} />
        <div data-thumb="lo" className={thumbClass} style={{ left: `calc(${pctLo}% - ${8 + sepPx}px)` }} />
        <div data-thumb="hi" className={thumbClass} style={{ left: `calc(${pctHi}% - ${8 - sepPx}px)` }} />
      </div>
      <div className="mt-1 flex items-center justify-between gap-1">
        <span className="text-2xs font-medium tabular-nums text-neutral-600">{domain[0]}</span>
        <span className="text-2xs font-medium tabular-nums text-neutral-600">{domain[maxIdx]}</span>
      </div>
    </div>
  );
}

// Demo-card field labels per gallery locale. The widgets' own UI
// strings (pills, Apply, hints, month/day names) come from the
// pickers' built-in EN/ES sets selected by their `locale` prop — the
// gallery host passes the active language through `render(h, w, locale)`.
const DEMO_DATE_LABELS = {
  en: { range: 'Range', pickDate: 'Pick a date', period: 'Period', group: 'Group' },
  es: { range: 'Rango', pickDate: 'Elige una fecha', period: 'Período', group: 'Agrupar' },
};

export function DemoDateRangePicker({ locale = 'en' }) {
  // Committed selection (what "Aplicar" / a chip / a calendar pick set).
  const [range, setRange] = useState({ start: null, end: null });
  const [activePresetId, setActivePresetId] = useState(null);
  // Pending window driven by the slider (discarded on dismiss).
  const [pending, setPending] = useState(null);

  // Zoomed snap-point domain — derives from the COMMITTED selection
  // only (anti-freeze invariant: a live drag never moves its own track).
  const domain = useMemo(() => {
    const [lo, hi] = demoZoomWindow(range.start, range.end);
    return demoWeekDomain(lo, hi);
  }, [range.start, range.end]);
  const maxIdx = domain.length - 1;

  const displayStart = pending ? pending.start : range.start;
  const displayEnd = pending ? pending.end : range.end;
  const sliderLo = displayStart ? demoNearestIndex(domain, displayStart) : 0;
  const sliderHi = displayEnd ? demoNearestIndex(domain, displayEnd) : maxIdx;

  return (
    <DateRangePicker
      label={(DEMO_DATE_LABELS[locale] ?? DEMO_DATE_LABELS.en).range}
      locale={locale}
      start={displayStart}
      end={displayEnd}
      presets={DEMO_PRESETS}
      activePresetId={pending ? undefined : activePresetId}
      onPresetSelect={(p) => {
        const resolved = demoResolvePreset(p.id);
        if (!resolved) return;
        setPending(null);
        setActivePresetId(p.id);
        setRange({ start: resolved[0], end: resolved[1] });
      }}
      sliderSlot={
        domain.length > 0 ? (
          <DemoPopoverDateSlider
            domain={domain}
            lo={Math.min(sliderLo, sliderHi)}
            hi={Math.max(sliderLo, sliderHi)}
            onChange={(lo, hi) => {
              setPending({
                start: demoIsoToDate(domain[lo]),
                end: demoIsoToDate(domain[hi]),
              });
            }}
          />
        ) : undefined
      }
      onApply={() => {
        if (!pending) return;
        setActivePresetId(null);
        setRange(pending);
        setPending(null);
      }}
      onOpenChange={(open) => {
        if (!open) setPending(null);
      }}
      onSelect={(start, end) => {
        setActivePresetId(null);
        setPending(null);
        setRange({ start, end });
      }}
    />
  );
}

export function DemoCalendarPicker({ locale = 'en' }) {
  const [selected, setSelected] = useState(null);
  const labels = DEMO_DATE_LABELS[locale] ?? DEMO_DATE_LABELS.en;
  return <CalendarPicker label={labels.pickDate} locale={locale} selected={selected} onSelect={setSelected} />;
}

export function DemoRelativeDatePicker({ locale = 'en' }) {
  const [value, setValue] = useState('30d');
  const labels = DEMO_DATE_LABELS[locale] ?? DEMO_DATE_LABELS.en;
  return <RelativeDatePicker label={labels.period} locale={locale} value={value} onChange={setValue} />;
}

export function DemoDateGroupToggle({ locale = 'en' }) {
  const [value, setValue] = useState('month');
  const labels = DEMO_DATE_LABELS[locale] ?? DEMO_DATE_LABELS.en;
  return <DateGroupToggle label={labels.group} locale={locale} value={value} onChange={setValue} />;
}

// ── Analytics widgets with filter semantics ─────────────────

export function DemoMultiSelectDropdown() {
  const [selected, setSelected] = useState(['Laptops', 'Printers']);
  return (
    <MultiSelectDropdown
      label="Categories"
      options={multiSelectOptions}
      selected={selected}
      onToggle={(opt) =>
        setSelected(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt])
      }
    />
  );
}

export function DemoSavedFilters() {
  const [active, setActive] = useState('active');
  return <SavedFilters presets={savedFilterPresets} active={active} onSelect={setActive} />;
}

// ── Motion / Transitions (bi-components side) ───────────────
//
// The Transitions gallery entries render through `TransitionDemo`
// (motion.jsx): live demo + Replay + reduced-motion toggle +
// "Used in / How / Export" metadata (plan §2.6). The demos for the
// dashboard-render primitives (FadeSwap, NumberTween, RefreshOverlay,
// HeightTransition + the SWR / drill / anti-jump pattern demos) live
// in dashboard-render's `motion/gallery-demos.tsx` — this package is
// below it in the dependency graph and cannot import them.

/* eslint-disable react-hooks/rules-of-hooks -- demo components below
   follow the same local-state pattern as the wrappers above */

// (imports at top of file cover useState/useEffect/useRef)
import {
  MOTION,
  Spinner,
  GlobalProgressBar,
  RefreshShimmer,
  TransitionDemo,
  ensureMotionStyles,
  useCountUp,
  usePrefersReducedMotion,
} from '../lib/motion.jsx';
import { brand } from '../brand.js';

/** Tokens visualized: one dot per duration races across a track with
 *  each easing, so the 120/180/280ms scale is directly comparable.
 *  Reduced motion → dots sit at the finish line, no race. */
function TokenRow({ label, ms, ease, reduced }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-24 shrink-0 font-mono text-2xs text-neutral-500">
        {label} · {ms}ms
      </span>
      <div className="relative h-4 flex-1 rounded-full bg-neutral-100">
        <span
          className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full"
          style={{
            background: brand.accent.DEFAULT,
            ...(reduced
              ? { left: 'calc(100% - 12px)' }
              : { left: 0, animation: `pmTokenSlide ${ms}ms ${ease} forwards` }),
          }}
        />
      </div>
    </div>
  );
}

/** Inner body — rendered INSIDE TransitionDemo's MotionConfig so the
 *  reduced-motion toggle reaches the raw keyframe usage. */
function MotionTokensBody() {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();
  return (
    <div className="space-y-2.5">
      <style>{`@keyframes pmTokenSlide { from { left: 0; } to { left: calc(100% - 12px); } }`}</style>
      <TokenRow label="durFast" ms={MOTION.durFast} ease={MOTION.easeOut} reduced={reduced} />
      <TokenRow label="durBase" ms={MOTION.durBase} ease={MOTION.easeOut} reduced={reduced} />
      <TokenRow label="durSlow" ms={MOTION.durSlow} ease={MOTION.easeInOut} reduced={reduced} />
      <p className="pt-1 text-2xs text-neutral-600">
        easeOut for entrances/content · easeInOut for reversible moves — press Replay to race them.
      </p>
    </div>
  );
}

export function DemoMotionTokens() {
  return (
    <TransitionDemo
      usedIn={['Every primitive: FadeSwap, NumberTween, RefreshOverlay, HeightTransition, spinners, and every entrance/redraw/re-grow hook']}
      how={`Single token set — durations {fast ${MOTION.durFast} · base ${MOTION.durBase} · slow ${MOTION.durSlow}}ms raced above, plus the ENTRANCE pair the race does not show because it is a different rhythm: ${MOTION.delayEntrance}ms of hold (delayEntrance — the settle beat after the zero frame paints) then ${MOTION.durEntrance}ms of grow (durEntrance, deliberately slower than durSlow: at 280ms the grow read as a blink). durBase is the REFETCH beat (morphs, fade-overs); durEntrance is the FIRST-LOAD beat. Easings easeOut ${MOTION.easeOut} / easeInOut ${MOTION.easeInOut}. Never hardcode a duration.`}
      exportName="MOTION"
    >
      <MotionTokensBody />
    </TransitionDemo>
  );
}

/** The 3 loading levels side by side, over BOTH portal backgrounds
 *  (light card + dark banner gradient) — the "spinners no se notan"
 *  fix is contrast + size, verified visually here. */
export function DemoSpinnerLevels() {
  return (
    <TransitionDemo
      usedIn={['Action buttons (export, search, load-more)', 'any control with an in-flight user action', 'PR-C sweep: dashboard config-load + drill panel, portal admin pages (AI Snapshots / Context Notes / Issues), builder list / preview / publish / loader panels']}
      how="Level 3 of the loading language: 16/20px SVG arc, stroke 2, accent magenta, mandatory aria-label. Reduced motion keeps the static arc (state feedback stays)."
      exportName="Spinner"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col items-start gap-3 rounded-lg border border-neutral-200 bg-white p-4">
          <span className="text-2xs font-bold uppercase tracking-wider text-neutral-600">Light card</span>
          <div className="flex items-center gap-4">
            <Spinner size={16} ariaLabel="Loading (16px)" />
            <Spinner size={20} ariaLabel="Loading (20px)" />
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-600"
            >
              <Spinner size={16} ariaLabel="Exporting" />
              Exporting…
            </button>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 rounded-lg bg-navy p-4">
          <span className="text-2xs font-bold uppercase tracking-wider text-white/75">Dark banner</span>
          <div className="flex items-center gap-4">
            <Spinner size={16} ariaLabel="Loading (16px, dark)" />
            <Spinner size={20} ariaLabel="Loading (20px, dark)" />
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-white/80">
              <Spinner size={16} ariaLabel="Refreshing filters" />
              Refreshing…
            </span>
          </div>
        </div>
      </div>
    </TransitionDemo>
  );
}

export function DemoGlobalProgressBar() {
  const [active, setActive] = useState(true);
  return (
    <TransitionDemo
      usedIn={['Portal header (any refetch in flight)', 'builder Preview modal (scoped to the modal top)']}
      how="Level 1: indeterminate 2px magenta hairline pinned to the top of the host chrome — always signals activity even when card-level feedback is subtle. Reduced motion renders a static solid hairline."
      exportName="GlobalProgressBar"
    >
      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        {/* fake portal header the bar pins to (fixed={false} to stay in-card) */}
        <GlobalProgressBar active={active} fixed={false} ariaLabel="Dashboard refreshing" />
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <div className="text-sm font-bold text-neutral-800">Gating — Executive</div>
            <div className="text-[11px] text-neutral-600">portal header (mock)</div>
          </div>
          <button
            type="button"
            onClick={() => setActive(a => !a)}
            className="rounded-md border border-neutral-200 px-2.5 py-1 text-[11px] font-semibold text-neutral-600 hover:border-accent/50 hover:text-accent transition-colors"
          >
            {active ? 'Stop fetch' : 'Start fetch'}
          </button>
        </div>
      </div>
    </TransitionDemo>
  );
}

export function DemoRefreshShimmer() {
  return (
    <TransitionDemo
      usedIn={['Inside RefreshOverlay (dashboard-render) — any card whose data is being refetched']}
      how="Level 2 CSS base: diagonal accent-tinted sweep, absolutely positioned over a relative card. The stateful wrapper is RefreshOverlay in dashboard-render: it dims the still-mounted content to 0.75 and only appears after 300ms of refreshing (calibración 2026-07-23 — 150ms + dim 0.55 let fast fetches paint a full luminance dip that read as parpadeo). Reduced motion drops the sweep and keeps the dim."
      exportName="RefreshShimmer"
    >
      <div className="relative overflow-hidden rounded-lg border border-neutral-200 bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-600">Active listings</div>
        <div className="mt-1 text-2xl font-bold text-neutral-800">9,650</div>
        <div className="text-[11px] text-neutral-600">+184 this week</div>
        <RefreshShimmer />
      </div>
    </TransitionDemo>
  );
}

export function DemoEntranceGrow() {
  return (
    <TransitionDemo
      usedIn={[
        'Country Health Scorecard — period-tile rings, country meters, retailer share bars, retail-detail columns',
        'Every MeterBar / StackedShareBar instance product-wide',
      ]}
      how={`useEntrance(): false on the first paint, true after the zero frame has PAINTED (double-rAF) + requestIdleCallback (SVG transitions paint on the main thread — starting during layout eats frames) + a ${MOTION.delayEntrance}ms pause; then a compositor-only CSS transition (transform / stroke-dashoffset, ${MOTION.durEntrance}ms easeOut) carries each element from zero to its value. Mount-once: later data changes morph in place, they never re-count. Non-negotiable guards (regla BI — a frozen intermediate number is a lie): hidden document mounts settled, hiding mid-flight snaps to the value, and a setTimeout backstop covers occluded-but-"visible" windows where Chrome pauses rAF with no event. Reduced motion mounts at the final value.`}
      exportName="useEntrance"
    >
      <div className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4">
        <MeterBar
          label="US"
          labelWidth={64}
          value={90.8}
          toneSpec={{ thresholds: [{ min: 90, tone: 'good' }, { min: 80, tone: 'warn' }, { min: null, tone: 'bad' }] }}
          showValue
          height={10}
          ariaLabel="US entrance demo"
        />
        <StackedShareBar
          segments={[
            { key: 'auth', label: 'Authorized', value: 908 },
            { key: 'no', label: 'No agreement', value: 92 },
          ]}
          height={12}
          ariaLabel="Entrance share demo"
        />
      </div>
    </TransitionDemo>
  );
}

export function DemoLineChartClickFilter() {
  // Cross-filter por punto (2026-08-10): el demo mantiene la semana
  // seleccionada en estado local y la muestra como chip — exactamente
  // lo que hace el host del dashboard con el filtro real.
  const [week, setWeek] = useState(null);
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-neutral-600">
        {week ? (
          <span>
            Filtro activo: <b>{week}</b>{' '}
            <button
              onClick={() => setWeek(null)}
              className="ml-1 rounded border border-neutral-300 px-1.5 text-2xs font-semibold hover:border-accent hover:text-accent"
            >
              ×
            </button>
          </span>
        ) : (
          'Haz clic en un punto para filtrar esa semana (clic de nuevo = quitar).'
        )}
      </div>
      <LineChartT
        data={lineData}
        height={170}
        onPointClick={(label) => setWeek((prev) => (prev === label ? null : label))}
        activePointLabel={week}
        clickHint="Clic para filtrar"
        ariaLabel="Línea clickeable demo"
      />
    </div>
  );
}

export function DemoMultiLineClickFilter() {
  const [week, setWeek] = useState(null);
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-neutral-600">
        {week ? (
          <span>
            Filtro activo: <b>{week}</b>{' '}
            <button
              onClick={() => setWeek(null)}
              className="ml-1 rounded border border-neutral-300 px-1.5 text-2xs font-semibold hover:border-accent hover:text-accent"
            >
              ×
            </button>
          </span>
        ) : (
          'Haz clic en un punto para filtrar esa semana (clic de nuevo = quitar).'
        )}
      </div>
      <MultiLineT
        series={multiLineSeries}
        labels={multiLineLabels}
        height={170}
        onPointClick={(label) => setWeek((prev) => (prev === label ? null : label))}
        activePointLabel={week}
        clickHint="Clic para filtrar"
        ariaLabel="Multi-línea clickeable demo"
      />
      <div className="text-[11px] text-neutral-600">
        Escala estrecha (una cuota entre 30 y 31 %): el eje toma los decimales del paso en vez de repetir 30 y 31.
      </div>
      <MultiLineT
        series={[
          { name: 'Cobertura', data: [30.2, 30.6, 30.9, 30.4, 30.8] },
          { name: 'Período anterior', data: [29.9, 30.1, 30.3, 30.2, 30.5], dashed: true },
        ]}
        labels={['S1', 'S2', 'S3', 'S4', 'S5']}
        height={150}
        ySuffix="%"
        ariaLabel="Multi-línea con escala estrecha demo"
      />
    </div>
  );
}

export function DemoEntranceEverywhere() {
  return (
    <TransitionDemo
      usedIn={[
        'Every chart in the library — entrance is ON by default; pass entrance={false} to opt out',
      ]}
      how={`EntranceGroup wraps the data marks only (never axes/text): bars growY from the baseline, horizontal bars growX, radial marks pop from center, density marks fade. Line-family strokes draw in left-to-right via useLineDraw (pathLength-normalized stroke-dashoffset). All ${MOTION.durEntrance}ms easeOut, compositor-only, reduced-motion mounts settled. Two modes (audit 2026-07-24): passing drawn={useGrowCycle(...)} gives a controlled, HOOK-FREE render (a 52-bar chart used to pay 52 matchMedia subscriptions for a value it discarded), and EVERY widget in the library is now here — charts since r16, analytics + maps migrated in the same audit, so all of them re-animate together when a filter lands. Omitting the prop selects the autonomous mode: the group runs its own useEntrance and enters once, never re-animating; no in-library caller uses it, it is the default of the public API. A call site must be consistent — switching mode swaps the component type and would remount the marks.`}
      exportName="EntranceGroup"
    >
      <div className="grid grid-cols-1 gap-4 rounded-lg border border-neutral-200 bg-white p-4 sm:grid-cols-3">
        <VerticalBar data={barData} height={150} ariaLabel="Entrance bars demo" />
        <LineChart data={lineData} height={150} ariaLabel="Entrance line draw-in demo" />
        <PieChart data={pieData} height={150} ariaLabel="Entrance pie pop demo" />
      </div>
    </TransitionDemo>
  );
}

// ── Refetch language (the four things, happening together) ──────
//
// THE entry the gallery was missing (audit 2026-07-24). Every other
// Transitions card demos ONE primitive; what the user actually
// calibrated over five rounds (2026-07-23/24) is the COMBINED gesture a
// filter change produces, and the only way to judge it is to see the
// four behaviours fire at once:
//
//   1. numbers MORPH from the value on screen (never re-count from 0)
//   2. lines are REDRAWN left-to-right (a line cannot morph honestly —
//      what it reveals IS the new data)
//   3. bar/radial charts RE-GROW, ALL marks in the same wave
//   4. tables cascade ONLY on their first landing — later row sets
//      (this button, pagination, sort, search) mount opaque
//
// Mount / Replay = FIRST LOAD (everything from zero, after the idle +
// delayEntrance beat). The button = REFETCH. No crossfade anywhere on
// the chart tier: that was turned off on 2026-07-23.

const REFETCH_SETS = [
  {
    id: 'FY26 Q1',
    kpiListings: 9650,
    kpiCompliance: 92.4,
    bars: [
      { label: 'Amazon', value: 4200 },
      { label: 'MediaMarkt', value: 3100 },
      { label: 'Currys', value: 1900 },
      { label: 'Worten', value: 950 },
    ],
    line: [
      { label: 'W1', value: 12000 }, { label: 'W2', value: 14000 },
      { label: 'W3', value: 11000 }, { label: 'W4', value: 16000 },
      { label: 'W5', value: 18000 }, { label: 'W6', value: 17500 },
    ],
    rows: [
      { seller: 'TechnoMax', listings: 1240, delta: 4 },
      { seller: 'CompuGlobal', listings: 986, delta: -2 },
      { seller: 'OfficePlus', listings: 733, delta: 7 },
      { seller: 'MegaStore', listings: 512, delta: -5 },
    ],
  },
  {
    id: 'FY26 Q2',
    kpiListings: 11460,
    kpiCompliance: 88.1,
    bars: [
      { label: 'Amazon', value: 5100 },
      { label: 'MediaMarkt', value: 2700 },
      { label: 'Currys', value: 2450 },
      { label: 'Worten', value: 1200 },
    ],
    line: [
      { label: 'W1', value: 17500 }, { label: 'W2', value: 15200 },
      { label: 'W3', value: 19800 }, { label: 'W4', value: 18100 },
      { label: 'W5', value: 22400 }, { label: 'W6', value: 24900 },
    ],
    rows: [
      { seller: 'CompuGlobal', listings: 1410, delta: 9 },
      { seller: 'TechnoMax', listings: 1102, delta: -3 },
      { seller: 'MegaStore', listings: 640, delta: 12 },
      { seller: 'OfficePlus', listings: 588, delta: -6 },
    ],
  },
];

const REFETCH_COLUMNS = [
  { key: 'seller', label: 'Seller' },
  { key: 'listings', label: 'Listings' },
  { key: 'delta', label: 'Δ vs prev' },
];

/** KPI value driven by the REAL product hook (`useCountUp`, the
 *  bi-components twin of dashboard-render's `NumberTween`): the
 *  entrance counts 0 → value, later targets morph from the number on
 *  screen. The formatter is deterministic, so the landed frame is
 *  byte-identical to the static path. */
function RefetchKpi({ label, value, decimals = 0, suffix = '' }) {
  const shown = useCountUp(value);
  const text =
    decimals > 0
      ? shown.toFixed(decimals)
      : Math.round(shown).toLocaleString();
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-2.5">
      <div className="text-2xs font-semibold uppercase tracking-wider text-neutral-600">
        {label}
      </div>
      <div className="mt-0.5 text-xl font-bold tabular-nums text-neutral-800">
        {text}{suffix}
      </div>
    </div>
  );
}

/** Inner body — rendered INSIDE TransitionDemo's MotionConfig so the
 *  reduced-motion toggle reaches every hook below. */
function RefetchLanguageBody() {
  const [idx, setIdx] = useState(0);
  const set = REFETCH_SETS[idx];
  const next = REFETCH_SETS[(idx + 1) % REFETCH_SETS.length];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setIdx(i => (i + 1) % REFETCH_SETS.length)}
          className="rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-600 transition-colors hover:border-accent/50 hover:text-accent"
        >
          Change filter (→ {next.id})
        </button>
        <span className="text-2xs text-neutral-500">
          showing <span className="font-semibold text-neutral-700">{set.id}</span> · Replay above = first load
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <RefetchKpi label="Active listings" value={set.kpiListings} />
        <RefetchKpi label="MAP compliance" value={set.kpiCompliance} decimals={1} suffix="%" />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-lg border border-neutral-200 bg-white p-2">
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wider text-neutral-600">
            Bars — re-grow together
          </div>
          <VerticalBar data={set.bars} height={130} ariaLabel="Refetch bars demo" />
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-2">
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wider text-neutral-600">
            Line — redrawn
          </div>
          <LineChart data={set.line} height={130} ariaLabel="Refetch line redraw demo" />
        </div>
      </div>

      <ComparisonTable
        columns={REFETCH_COLUMNS}
        rows={set.rows}
        compareKeys={['delta']}
        title="Sellers — cascade only on the first landing"
        searchable={false}
        onExport={false}
        pageSize={10}
      />

      <ol className="list-decimal space-y-0.5 pl-4 text-2xs leading-snug text-neutral-600">
        <li>Numbers morph from the value on screen — they never restart at zero.</li>
        <li>The line is redrawn left-to-right (its shape IS the new data).</li>
        <li>Every bar re-grows in the SAME wave — survivors and newcomers alike.</li>
        <li>The table swaps rows opaque: the cascade already played on its first landing.</li>
      </ol>
    </div>
  );
}

export function DemoRefetchLanguage() {
  return (
    <TransitionDemo
      usedIn={[
        'EVERY filter / cross-filter change in the portal and the builder Preview — this is the combined gesture, not one primitive',
        'The same four rules apply inside a drill panel and after a cross-filter click',
      ]}
      how={`One gesture, four rules (calibrated with the user over 5 rounds, 2026-07-23/24). FIRST LOAD (mount / Replay): everything enters from zero after the zero frame paints + an idle beat + ${MOTION.delayEntrance}ms, over ${MOTION.durEntrance}ms easeOut. REFETCH (the button): (1) numbers MORPH from the value on screen over ${MOTION.durBase}ms — useCountUp here, NumberTween in dashboard-render — never a dry snap and never a re-count from zero; (2) lines are REDRAWN left-to-right (useLineDraw keyed by lineDataSig — a line cannot morph honestly, what it reveals IS the new data); (3) bar/radial marks RE-GROW ALL TOGETHER (useGrowCycle resets the whole group for one painted frame, never per-item, which mixed old and new states while filtering); (4) tables cascade ONLY on their first non-empty landing — this button, pagination, sort and search mount rows opaque. Charts do NOT crossfade: the FadeSwap of the chart tier ships disabled since 2026-07-23. Same data ⇒ same signature ⇒ nothing replays (the first-page cache returns identical payloads in ~10ms). Reduced motion lands every value instantly, and so do hidden documents, occluded windows (setTimeout backstop — Chrome pauses rAF without an event) and FadeSwap ghosts: an intermediate frame frozen on screen would be a number that is not the datum.`}
      exportName="useGrowCycle + useLineDraw + useCountUp + useRowCascade"
    >
      <RefetchLanguageBody />
    </TransitionDemo>
  );
}

export function DemoSparkleHover() {
  return (
    <TransitionDemo
      usedIn={['Portal banner — the "+ Show AI Analysis" button']}
      how="pmSparkle keyframe (rotate + scale twinkle, 0.9s loop) on the Sparkles icon, gated behind motion-safe:group-hover; the pill fills with the brand accent magenta on hover."
      exportName="pmSparkle (keyframe via ensureMotionStyles)"
    >
      <div className="rounded-xl bg-navy px-5 py-4">
        <button
          type="button"
          className="group inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-3 py-1 text-xs font-medium text-white transition-colors hover:border-accent hover:bg-accent"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="motion-safe:group-hover:animate-[pmSparkle_0.9s_ease-in-out_infinite]"
          >
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          </svg>
          + Show AI Analysis
        </button>
        <p className="mt-2 text-[11px] text-white/75">Hover the pill — magenta fill + twinkling icon.</p>
      </div>
    </TransitionDemo>
  );
}

export function DemoSegmentedTabs() {
  const [category, setCategory] = useState('supplies');
  const [country, setCountry] = useState('us');
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-neutral-600">Category</p>
        <SegmentedTabs
          options={categoryTabs}
          value={category}
          onChange={setCategory}
          shape="pill"
          fill="inline"
          ariaLabel="Category"
        />
      </div>
      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-neutral-600">
          Country — full width, counts inline, strong tone (the report's axes)
        </p>
        <SegmentedTabs
          options={countryTabs}
          value={country}
          onChange={setCountry}
          shape="rect"
          fill="stretch"
          tone="navy"
          ariaLabel="Country"
        />
      </div>
      <p className="text-2xs leading-snug text-neutral-500">
        Es un radiogroup, no un tablist: las flechas mueven la selección y Tab
        entra y sale del control de una vez. Look v3: una pista gris; la opción
        elegida se eleva en blanco, o va en tinta con tone navy / inset (ejes del
        reporte y cajón de filtros). Seleccionado: {category} / {country}.
      </p>
    </div>
  );
}
